import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import {
  purchaseNeedsWeightRecording,
  purchaseWeighStepLabel,
} from "../../lib/unloadingHelpers";
import { PageHeader, KpiTile, SectionCard, StatePill, Pill } from "../../components/Ui";
import { ArrowRight } from "lucide-react";
import MobilePillTabBar from "../../components/mobile/MobilePillTabBar";

export default function WeighbridgeDashboard() {
  const [queueFilter, setQueueFilter] = useState("all");
  const { data, reload } = usePageBootstrap({
    path: "/dashboard/bootstrap",
    wsTypes: ["purchase", "gate_pass"],
  });
  useBootstrapWS(["purchase", "gate_pass"], reload);

  const readyToStart = data?.ready_to_start || [];
  const weightQueue = (data?.weight_queue || []).filter(purchaseNeedsWeightRecording);
  const recentWeighments = data?.recent_weighments || [];
  const outwardCount = data?.outward_queue_count ?? 0;

  const filterTabs = useMemo(
    () => [
      {
        id: "all",
        label: "All",
        badge: weightQueue.length + outwardCount + readyToStart.length,
      },
      { id: "inward", label: "Inward", badge: weightQueue.length },
      { id: "outward", label: "Outward", badge: outwardCount },
      { id: "recent", label: "Recent", badge: recentWeighments.length },
    ],
    [weightQueue.length, outwardCount, readyToStart.length, recentWeighments.length],
  );

  const sectionClass = (id) =>
    queueFilter !== "all" && queueFilter !== id ? "hidden lg:block" : undefined;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="weighbridge-dashboard">
      <PageHeader
        eyebrow="Weighbridge"
        title="Weighbridge Desk"
        description="Pending inward and outward weights, vehicles ready to start, and recent bridge recordings."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <KpiTile label="Record weight" value={weightQueue.length} sub="Inward at bridge" accent testId="wb-kpi-weight" />
        <KpiTile label="Outward queue" value={outwardCount} sub="Tare + gross pending" testId="wb-kpi-outward" />
        <KpiTile label="Ready to start" value={readyToStart.length} sub="Gate pass, no purchase" testId="wb-kpi-start" />
        <KpiTile label="Recent" value={recentWeighments.length} sub="Weighments logged" testId="wb-kpi-recent" />
      </div>

      <MobilePillTabBar
        tabs={filterTabs}
        value={queueFilter}
        onChange={setQueueFilter}
        testId="wb-dashboard-queue-filter"
      />

      <SectionCard title="Inward — pending weights" testId="wb-inward-queue" className={sectionClass("inward")}>
        {weightQueue.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No trucks awaiting weight recording.</p>
        ) : (
          <div className="space-y-2">
            {weightQueue.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                to="/weighbridge/inward"
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 hover:bg-accent/60 transition-colors"
                data-testid={`wb-queue-${p.code}`}
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{p.code}</div>
                  <div className="text-sm truncate">{p.party_name}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Pill tone="gold">{purchaseWeighStepLabel(p)}</Pill>
                  <StatePill state={p.state} />
                </div>
              </Link>
            ))}
          </div>
        )}
        <Link to="/weighbridge/inward" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
          Open inward weighment <ArrowRight className="h-3 w-3" />
        </Link>
      </SectionCard>

      {(outwardCount > 0 || queueFilter === "outward" || queueFilter === "all") && (
        <SectionCard title="Outward — pending weights" testId="wb-outward-queue" className={sectionClass("outward")}>
          <p className="text-sm text-muted-foreground mb-3">
            {outwardCount} outward dispatch(es) awaiting tare or gross at the bridge.
          </p>
          <Link to="/weighbridge/outward" className="inline-flex items-center gap-1 text-xs link-brand">
            Open outward weighment <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>
      )}

      <SectionCard title="Recent weighments" testId="wb-recent" className={sectionClass("recent")}>
        {recentWeighments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No weighments recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {recentWeighments.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                to="/weighbridge/register"
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 hover:bg-accent/60"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{p.code}</div>
                  <div className="text-sm truncate">
                    {p.vehicle_number || "—"} · {p.party_name}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground tabular shrink-0">
                  {p.net_weight_qtl != null ? `${p.net_weight_qtl} qtl` : "—"}
                </div>
              </Link>
            ))}
          </div>
        )}
        <Link to="/weighbridge/register" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
          Full register <ArrowRight className="h-3 w-3" />
        </Link>
      </SectionCard>

      {readyToStart.length > 0 && (
        <SectionCard title="Vehicles awaiting purchase start" testId="wb-ready-start" className={sectionClass("inward")}>
          <div className="space-y-2">
            {readyToStart.slice(0, 6).map((g) => (
              <div key={g.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-sm">
                <span className="font-mono">{g.code}</span>
                <span className="text-muted-foreground truncate ml-2">{g.vehicle_number} · {g.party_name || "—"}</span>
              </div>
            ))}
          </div>
          <Link to="/weighbridge/inward" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Start on inward tab <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>
      )}
    </div>
  );
}
